import React from 'react';
import PropTypes from 'prop-types';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import { createFilterOptions as muiCreateFilterOptions } from '@material-ui/lab/useAutocomplete';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useFormContext } from '../Form';
import ErrorMessage from '../ErrorMessage';
import Icon from '../Icon';
import translations from './translations';
import parse from '@afosto/utils/autocomplete/parse';
import match from '@afosto/utils/autocomplete/match';
import clsx from 'clsx';
import styled from 'styled-components';
import useRegisterInput from '../Form/useRegisterInput';

const AutocompleteContainer = styled(Box)`
  && {
    &.inline {
      &:hover {
        .inlineEditField-adornment {
          visibility: visible !important;
          opacity: 1;
        }
      }
    }
  }
`;

const HoverTextField = styled(TextField)`
  && {
    &.inline {
      &:hover {
        .inlineEditField-adornment {
          visibility: visible !important;
          opacity: 1;
        }
      }

      .MuiSelect-icon {
        opacity: 1;
        transition: ${props =>
          props.theme.transitions.create('opacity', {
            easing: props.theme.transitions.easing.sharp,
            duration: props.theme.transitions.duration.shortest,
          })};
      }

      .MuiOutlinedInput-notchedOutline {
        transition: ${props =>
          props.theme.transitions.create('border-color', {
            easing: props.theme.transitions.easing.sharp,
            duration: props.theme.transitions.duration.shortest,
          })};
      }

      .MuiInputBase-root.Mui-disabled {
        color: ${props => props.theme.palette.text.primary};

        .MuiSelect-icon {
          opacity: 0;
        }

        .MuiOutlinedInput-notchedOutline {
          border-color: transparent;
        }
      }
    }
  }
`;

const HiddenIconButton = styled(IconButton)`
  && {
    visibility: hidden;
    opacity: 0;
    transition: ${props =>
      props.theme.transitions.create('opacity', {
        easing: props.theme.transitions.easing.sharp,
        duration: props.theme.transitions.duration.shortest,
      })};
  }
`;

export const createFilterOptions = muiCreateFilterOptions;

/**
 * Add's span elements with a bold font-size around the substring `value`
 * and returns the string as a React component.
 * @param {string} string
 * @param {string} value
 * @returns {*}
 */
export const highlightSubstring = (string, value) => {
  const matches = match(string, value, { insideWords: true, findAllOccurrences: true });
  const parts = parse(string, matches);

  return (
    <>
      {parts.map((part, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`${part}-${index}`} style={{ fontWeight: part.highlight ? 700 : 400 }}>
          {part.text.replace(/ /g, '\u00a0')}
        </span>
      ))}
    </>
  );
};

/**
 * Test if an option is selected. Can handle object and strings for both parameters.
 * @param {object, string} option
 * @param {object, string} value
 * @returns {boolean}
 */
const defaultGetOptionSelected = (option, value) => {
  const parsedOption = option?.value ? option.value : option;
  const parsedValue = value?.value ? value.value : value;

  return parsedOption === parsedValue;
};

/**
 * Gets the label from the selected options. Can handle strings and objects.
 * @param {object, string} option
 * @returns {String}
 */
const defaultGetOptionLabel = option => {
  if (typeof option === 'object') {
    return option?.label;
  }

  return option;
};

/**
 *
 * @param {object, string} option
 * @param inputValue
 * @returns {*}
 */
const defaultRenderOption = (option, { inputValue = '' } = {}) => {
  let label = option;

  if (typeof option === 'object') {
    // eslint-disable-next-line prefer-destructuring
    label = option?.label;
  }

  return highlightSubstring(label, inputValue);
};

const Autocomplete = ({
  autocomplete,
  autoComplete,
  autoFocus,
  autoHighlight,
  autoSelect,
  blurOnSelect,
  ChipProps,
  clearOnEscape,
  clearText,
  closeIcon,
  debug,
  defaultValue,
  disableClearable,
  disableCloseOnSelect,
  disabled,
  disableListWrap,
  disablePortal,
  error,
  errorMessage,
  enableCreate,
  filterOptions,
  filterSelectedOptions,
  forcePopupIcon,
  fullWidth,
  getOptionDisabled,
  getOptionLabel,
  getOptionSelected,
  groupBy,
  helperText,
  id,
  includeInputInList,
  inline,
  InputLabelProps,
  InputProps,
  inputValue,
  inputRef,
  isEditing,
  label,
  ListboxComponent,
  ListboxProps,
  loading,
  loadingText,
  margin,
  multiple,
  name,
  noOptionsText,
  onChange,
  onBlur,
  onClose,
  onEdit,
  onInputChange,
  onOpen,
  open,
  openText,
  options,
  PaperComponent,
  PopperComponent,
  popupIcon,
  placeholder,
  readOnly,
  register: shouldRegister,
  renderGroup,
  renderInput,
  renderOption,
  renderTags,
  size,
  value,
  variant,
}) => {
  useRegisterInput(name, shouldRegister);

  const intl = useIntl();
  const formContext = useFormContext();
  const {
    editInline: formInline = false,
    isEditing: formIsEditing = true,
    errors = {},
    watch,
    setValue,
    setIsEditing,
  } = formContext || {};

  const fallbackValue = multiple ? [] : null;
  const parsedInputProps = {
    inline: inline ?? formInline,
    isEditing: isEditing ?? formIsEditing,
    error: error ?? !!errors[name],
    value: value ?? (watch ? watch(name) ?? fallbackValue : fallbackValue),
    InputProps,
    InputLabelProps,
    disabled,
    ChipProps,
    forcePopupIcon,
  };

  const isInline = parsedInputProps.inline && !parsedInputProps.isEditing;
  const showEditAdornment = isInline && !readOnly;

  if (isInline || readOnly) {
    parsedInputProps.disabled = true;
    parsedInputProps.ChipProps = {
      ...ChipProps,
      onDelete: undefined,
      disabled: false,
    };
    parsedInputProps.forcePopupIcon = false;
    parsedInputProps.InputLabelProps = {
      ...(parsedInputProps.InputLabelProps || {}),
      shrink: true,
    };
  }

  /**
   * Handle the change on the select.
   * @param {object|Event} ev
   * @param {any} newValue
   */
  const handleOnChange = (ev, newValue) => {
    if (formContext && !onChange) {
      setValue(name, newValue);
      return;
    }

    if (onChange) {
      onChange(ev, newValue);
    }
  };

  /**
   * On edit handler.
   */
  const handleOnEdit = () => {
    if (formContext && !onEdit) {
      setIsEditing(true);
    }

    if (onEdit) {
      onEdit();
    }
  };

  /**
   * Return the error message.
   */
  const getErrorMessage = () => {
    if (formContext && !errorMessage) {
      return errors[name] && <ErrorMessage errors={errors} name={name} />;
    }

    return errorMessage;
  };

  return (
    <AutocompleteContainer
      position="relative"
      className={clsx({ inline: isInline || (parsedInputProps.inline && readOnly) })}
    >
      <MuiAutocomplete
        autoComplete={autoComplete}
        autoHighlight={autoHighlight}
        autoSelect={autoSelect}
        blurOnSelect={blurOnSelect}
        ChipProps={parsedInputProps.ChipProps}
        clearOnEscape={clearOnEscape}
        clearText={clearText || intl.formatMessage(translations.clearText)}
        closeIcon={closeIcon}
        debug={debug}
        defaultValue={defaultValue}
        disableClearable={disableClearable}
        disableCloseOnSelect={disableCloseOnSelect}
        disabled={parsedInputProps.disabled}
        disableListWrap={disableListWrap}
        disablePortal={disablePortal}
        filterOptions={filterOptions}
        filterSelectedOptions={filterSelectedOptions}
        forcePopupIcon={parsedInputProps.forcePopupIcon}
        freeSolo={enableCreate}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        groupBy={groupBy}
        id={id}
        includeInputInList={includeInputInList}
        inputValue={inputValue}
        ListboxComponent={ListboxComponent}
        ListboxProps={ListboxProps}
        loading={loading}
        loadingText={loadingText || intl.formatMessage(translations.loadingText)}
        multiple={multiple}
        noOptionsText={noOptionsText || intl.formatMessage(translations.noOptionsText)}
        onBlur={onBlur}
        onChange={handleOnChange}
        onClose={onClose}
        onInputChange={onInputChange}
        onOpen={onOpen}
        open={open}
        openText={openText || intl.formatMessage(translations.openText)}
        options={options}
        PaperComponent={PaperComponent}
        PopperComponent={PopperComponent}
        popupIcon={popupIcon}
        renderGroup={renderGroup}
        renderInput={
          renderInput ||
          (params => (
            <HoverTextField
              {...params}
              name={name}
              className={clsx({ inline: isInline || (parsedInputProps.inline && readOnly) })}
              autoComplete={autocomplete}
              autoFocus={autoFocus}
              error={parsedInputProps.error}
              fullWidth={fullWidth}
              helperText={parsedInputProps.error ? getErrorMessage() : helperText}
              label={label}
              margin={margin}
              placeholder={placeholder}
              readOnly={readOnly}
              variant={variant}
              inputRef={inputRef}
              notched={inline || undefined}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...parsedInputProps.InputLabelProps,
              }}
              InputProps={{
                ...params.InputProps,
                ...parsedInputProps.InputProps,
                inputProps: {
                  ...params.inputProps,
                  autoComplete: autocomplete,
                },
              }}
            />
          ))
        }
        renderOption={renderOption}
        renderTags={
          renderTags
            ? (values, getTagProps) => renderTags(values, getTagProps, parsedInputProps.ChipProps)
            : undefined
        }
        size={size}
        value={parsedInputProps.value}
      />
      {showEditAdornment && (
        <Box
          position="absolute"
          mt={0.5}
          top="50%"
          right="0.5rem"
          style={{ transform: 'translateY(-50%)' }}
        >
          <HiddenIconButton className="inlineEditField-adornment" onClick={handleOnEdit} edge="end">
            <Icon icon={['fal', 'pencil']} fontSize="small" />
          </HiddenIconButton>
        </Box>
      )}
    </AutocompleteContainer>
  );
};

Autocomplete.propTypes = {
  autocomplete: PropTypes.string,
  autoComplete: PropTypes.bool,
  autoFocus: PropTypes.bool,
  autoHighlight: PropTypes.bool,
  autoSelect: PropTypes.bool,
  blurOnSelect: PropTypes.oneOfType([PropTypes.oneOf(['mouse', 'touch']), PropTypes.bool]),
  ChipProps: PropTypes.object,
  clearOnEscape: PropTypes.bool,
  clearText: PropTypes.string,
  closeIcon: PropTypes.node,
  debug: PropTypes.bool,
  defaultValue: PropTypes.any,
  disableClearable: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  disableListWrap: PropTypes.bool,
  disablePortal: PropTypes.bool,
  enableCreate: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  filterOptions: PropTypes.func,
  filterSelectedOptions: PropTypes.bool,
  forcePopupIcon: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.bool]),
  fullWidth: PropTypes.bool,
  getOptionDisabled: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionSelected: PropTypes.func,
  groupBy: PropTypes.func,
  id: PropTypes.string,
  helperText: PropTypes.string,
  includeInputInList: PropTypes.bool,
  inline: PropTypes.bool,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
  inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  inputValue: PropTypes.string,
  isEditing: PropTypes.bool,
  label: PropTypes.string,
  ListboxComponent: PropTypes.elementType,
  ListboxProps: PropTypes.object,
  loading: PropTypes.bool,
  loadingText: PropTypes.node,
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),
  multiple: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noOptionsText: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onInputChange: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  openText: PropTypes.string,
  options: PropTypes.array,
  PaperComponent: PropTypes.elementType,
  PopperComponent: PropTypes.elementType,
  popupIcon: PropTypes.node,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  register: PropTypes.bool,
  renderGroup: PropTypes.func,
  renderInput: PropTypes.func,
  renderOption: PropTypes.func,
  renderTags: PropTypes.func,
  size: PropTypes.oneOf(['medium', 'small']),
  value: PropTypes.any,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

Autocomplete.defaultProps = {
  autocomplete: 'new-street-address',
  autoComplete: true,
  autoFocus: false,
  autoHighlight: true,
  autoSelect: false,
  blurOnSelect: false,
  ChipProps: undefined,
  clearOnEscape: false,
  clearText: undefined,
  closeIcon: <Icon icon={['fal', 'times']} size="small" />,
  debug: false,
  defaultValue: undefined,
  disableClearable: false,
  disableCloseOnSelect: false,
  disabled: false,
  disableListWrap: false,
  disablePortal: false,
  enableCreate: false,
  error: undefined,
  errorMessage: undefined,
  filterOptions: undefined,
  filterSelectedOptions: false,
  forcePopupIcon: true,
  fullWidth: true,
  getOptionDisabled: undefined,
  getOptionLabel: defaultGetOptionLabel,
  getOptionSelected: defaultGetOptionSelected,
  groupBy: undefined,
  id: undefined,
  helperText: undefined,
  includeInputInList: false,
  inline: undefined,
  InputLabelProps: undefined,
  InputProps: {},
  inputRef: undefined,
  inputValue: undefined,
  isEditing: undefined,
  label: undefined,
  ListboxComponent: undefined,
  ListboxProps: undefined,
  loading: false,
  loadingText: undefined,
  margin: 'normal',
  multiple: false,
  noOptionsText: undefined,
  onBlur: undefined,
  onChange: undefined,
  onClose: undefined,
  onEdit: undefined,
  onInputChange: undefined,
  onOpen: undefined,
  open: undefined,
  openText: undefined,
  options: undefined,
  PaperComponent: undefined,
  PopperComponent: undefined,
  popupIcon: <Icon icon={['fal', 'angle-down']} />,
  placeholder: undefined,
  readOnly: false,
  register: true,
  renderGroup: undefined,
  renderInput: undefined,
  renderOption: defaultRenderOption,
  renderTags: undefined,
  size: 'medium',
  value: undefined,
  variant: 'outlined',
};

export default Autocomplete;
